import type { DevicesAnalysis_GetDevicesQuery } from '@/__generated__/graphql';
import { getMetricFieldAttributes } from '@/utils/attributes/metricField';
import { getUnit } from '@/utils/units';
import { I18n } from '@lingui/core';

export const dataToTable = (data: DevicesAnalysis_GetDevicesQuery, i18n: I18n) => {
  if (!data?.devices) {
    return { headers: [], rows: [] };
  }

  const timestampMap = new Map<number, Record<string, number | string | null>>();

  const uniqueHeaders = new Set<string>();

  for (const device of data.devices) {
    if (!device?.profile || !device.metrics) {
      continue;
    }

    const deviceIdentifier = `${device.id} - ${device.profile.name}`;

    for (const [metricName, metricData] of Object.entries(device.metrics)) {
      if (
        typeof metricData === 'object' &&
        !Array.isArray(metricData) &&
        'data' in metricData &&
        Array.isArray(metricData.data)
      ) {
        let header = '';
        if (data.devices.length > 1) {
          header += `${deviceIdentifier} - `;
        }

        if ('metricField' in metricData) {
          const metricAttr = getMetricFieldAttributes(metricData.metricField, i18n);
          header += metricAttr.dataLabel;
        } else {
          header += metricName;
        }
        header += ' ';

        if ('unit' in metricData) {
          const unit = getUnit(metricData.unit, i18n);
          header += '(';
          header += unit ? unit.short : metricData.unit;
          header += ')';
        }

        uniqueHeaders.add(header);

        for (const point of metricData.data) {
          const value = Array.isArray(point) ? point[1] : point.value;
          const timestamp = Array.isArray(point) ? point[0] : point.timestamp;

          if (!timestampMap.has(timestamp)) {
            timestampMap.set(timestamp, {});
          }

          const currentTimestampData = timestampMap.get(timestamp)!;
          currentTimestampData[header] = value;
        }
      }
    }
  }

  const finalHeaders = ['Fecha', ...Array.from(uniqueHeaders).sort()];

  const sortedTimestamps = Array.from(timestampMap.keys()).sort((a, b) => a - b);

  const rows: (string | number | Date | null)[][] = [];

  for (const timestamp of sortedTimestamps) {
    const rowData = timestampMap.get(timestamp)!;

    const newRow: (string | number | null)[] = [timestamp];

    for (let i = 1; i < finalHeaders.length; i++) {
      const header = finalHeaders[i];
      const value = rowData[header] ?? null;
      newRow.push(value);
    }

    rows.push(newRow);
  }

  for (const row of rows) {
    if (typeof row[0] === 'number') {
      row[0] = new Date(row[0]);
    }
  }

  return { headers: finalHeaders, rows };
};
